/**
 * T-26: Service Worker Registration and Management
 * 
 * Handles service worker registration, updates, and offline capabilities
 */

export interface ServiceWorkerConfig {
    onUpdate?: (registration: ServiceWorkerRegistration) => void;
    onSuccess?: (registration: ServiceWorkerRegistration) => void;
    onError?: (error: Error) => void;
    onOffline?: () => void;
    onOnline?: () => void;
  }
  
  export interface CacheInfo {
    cacheSize: number;
    isOnline: boolean;
    hasUpdate: boolean;
  }
  
  // Global state
  let swRegistration: ServiceWorkerRegistration | null = null;
  let hasUpdate = false;
  
  /**
   * Register service worker for PWA functionality
   */
  export async function registerServiceWorker(config: ServiceWorkerConfig = {}): Promise<ServiceWorkerRegistration | null> {
    // Check if service workers are supported
    if (!('serviceWorker' in navigator)) {
      console.log('[SW] Service Worker not supported in this browser');
      config.onError?.(new Error('Service Worker not supported'));
      return null;
    }
  
    try {
      console.log('[SW] Registering service worker...');
      
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
        updateViaCache: 'none' // Always check for updates
      });
  
      swRegistration = registration;
  
      // Handle different registration events
      if (registration.installing) {
        console.log('[SW] Service worker installing...');
        handleServiceWorkerUpdate(registration, config);
      } else if (registration.waiting) {
        console.log('[SW] Service worker waiting...');
        hasUpdate = true;
        config.onUpdate?.(registration);
      } else if (registration.active) {
        console.log('[SW] Service worker active');
        config.onSuccess?.(registration);
      }
  
      // Listen for updates
      registration.addEventListener('updatefound', () => {
        console.log('[SW] Service worker update found');
        handleServiceWorkerUpdate(registration, config);
      });
  
      // Setup network status monitoring
      setupNetworkMonitoring(config);
  
      return registration;
  
    } catch (error) {
      console.error('[SW] Service worker registration failed:', error);
      config.onError?.(error as Error);
      return null;
    }
  }
  
  /**
   * Handle service worker updates
   */
  function handleServiceWorkerUpdate(registration: ServiceWorkerRegistration, config: ServiceWorkerConfig) {
    const newWorker = registration.installing;
    if (!newWorker) return;
  
    newWorker.addEventListener('statechange', () => {
      if (newWorker.state === 'installed') {
        if (navigator.serviceWorker.controller) {
          // New version available
          console.log('[SW] New version available');
          hasUpdate = true;
          config.onUpdate?.(registration);
        } else {
          // First time install
          console.log('[SW] Content cached for offline use');
          config.onSuccess?.(registration);
        }
      }
    });
  }
  
  /**
   * Apply service worker update
   */
  export function applyServiceWorkerUpdate(): void {
    if (!swRegistration || !swRegistration.waiting) {
      console.warn('[SW] No service worker update available');
      return;
    }
  
    // Tell the waiting service worker to skip waiting
    swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
    
    // Reload the page to get the new version
    window.location.reload();
  }
  
  /**
   * Setup network status monitoring
   */
  function setupNetworkMonitoring(config: ServiceWorkerConfig) {
    // Initial status
    if (!navigator.onLine) {
      config.onOffline?.();
    }
  
    // Listen for network changes
    window.addEventListener('online', () => {
      console.log('[SW] Network back online');
      config.onOnline?.();
    });
  
    window.addEventListener('offline', () => {
      console.log('[SW] Network went offline');
      config.onOffline?.();
    });
  }
  
  /**
   * Check if the app is currently offline
   */
  export function isOffline(): boolean {
    return !navigator.onLine;
  }
  
  /**
   * Check if there's a service worker update available
   */
  export function hasServiceWorkerUpdate(): boolean {
    return hasUpdate;
  }
  
  /**
   * Get cache information
   */
  export async function getCacheInfo(): Promise<CacheInfo> {
    let cacheSize = 0;
    
    if (swRegistration && swRegistration.active) {
      try {
        // Use MessageChannel to communicate with service worker
        const messageChannel = new MessageChannel();
        const response = await new Promise<{cacheSize: number}>((resolve) => {
          messageChannel.port1.onmessage = (event) => {
            resolve(event.data);
          };
          
          swRegistration!.active!.postMessage(
            { type: 'GET_CACHE_SIZE' },
            [messageChannel.port2]
          );
        });
        
        cacheSize = response.cacheSize;
      } catch (error) {
        console.warn('[SW] Failed to get cache size:', error);
      }
    }
  
    return {
      cacheSize,
      isOnline: navigator.onLine,
      hasUpdate: hasUpdate
    };
  }
  
  /**
   * Clear all caches (for debugging/reset)
   */
  export async function clearAllCaches(): Promise<void> {
    if (swRegistration && swRegistration.active) {
      try {
        const messageChannel = new MessageChannel();
        await new Promise<void>((resolve) => {
          messageChannel.port1.onmessage = () => {
            resolve();
          };
          
          swRegistration!.active!.postMessage(
            { type: 'CLEAR_CACHE' },
            [messageChannel.port2]
          );
        });
        
        console.log('[SW] All caches cleared');
      } catch (error) {
        console.warn('[SW] Failed to clear caches:', error);
        throw error;
      }
    }
  }
  
  /**
   * Check if the app can work offline
   */
  export async function checkOfflineCapability(): Promise<boolean> {
    try {
      // Check if essential resources are cached
      const cache = await caches.open('number-line-static-v1.0.0');
      const cachedUrls = await cache.keys();
      
      // Look for essential cached resources
      const hasCachedApp = cachedUrls.some(request => 
        request.url.includes('/') || request.url.includes('index.html')
      );
      
      return hasCachedApp;
    } catch (error) {
      console.warn('[SW] Failed to check offline capability:', error);
      return false;
    }
  }
  
  /**
   * Unregister service worker (for development/testing)
   */
export async function unregisterServiceWorker(): Promise<boolean> {
    if (!('serviceWorker' in navigator)) {
      return false;
    }
  
    try {
      // Prefer a tracked registration if available
      if (swRegistration) {
        const result = await swRegistration.unregister();
        console.log('[SW] Service worker (tracked) unregistered');
        swRegistration = null;
        hasUpdate = false;
        return result;
      }
  
      // Fall back to unregistering any registrations in scope (dev cleanup)
      const registrations = await navigator.serviceWorker.getRegistrations();
      let didUnregister = false;
      await Promise.all(
        registrations.map(async (registration) => {
          // Be conservative: only unregister our app's sw.js
          const scriptUrl = (registration as any).active?.scriptURL 
            || (registration as any).installing?.scriptURL 
            || (registration as any).waiting?.scriptURL 
            || '';
          if (scriptUrl.includes('/sw.js')) {
            const result = await registration.unregister();
            if (result) didUnregister = true;
          }
        })
      );
  
      if (didUnregister) {
        console.log('[SW] Existing service worker registrations unregistered');
      }
      hasUpdate = false;
      return didUnregister;
    } catch (error) {
      console.error('[SW] Failed to unregister service worker(s):', error);
      return false;
    }
  }